import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button, Select } from '../../../components/ui';
import { api } from '../../../lib/api';
import { AgreementModal, type AgreementFormDefaults } from '../../agreements/AgreementModal';
import type { AgreementRow, AgreementType } from '../../agreements/api';
import { NewClientModal } from '../../clients/components/NewClientModal';
import { useInvoiceClients } from '../api';

const TYPE_LABEL: Record<AgreementType, string> = {
  PROJECT: 'Project',
  SUPPORT: 'Support',
  RECURRING: 'Recurring',
  INTERNAL: 'Internal',
};

/**
 * Which client and agreement this invoice is FOR — kept in the URL
 * (`?clientId=&agreementId=`), so switching between Prepaid hours,
 * Installments, Time & materials and Custom keeps what was chosen, and a
 * link from a client or an agreement lands with it already picked.
 */
export function useInvoiceTarget() {
  const [params, setParams] = useSearchParams();
  const clientId = params.get('clientId') ?? '';
  const agreementId = params.get('agreementId') ?? '';
  const setTarget = (next: { clientId: string; agreementId: string }) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next.clientId) p.set('clientId', next.clientId);
        else p.delete('clientId');
        if (next.agreementId) p.set('agreementId', next.agreementId);
        else p.delete('agreementId');
        return p;
      },
      { replace: true },
    );
  return { clientId, agreementId, setTarget };
}

/**
 * A client's ACTIVE agreements. The same query key `/agreements`' client
 * filter uses, so it is shared and refreshed with every agreement write — but
 * never fired without a client (an empty `clientId` would fetch them all).
 */
export function useClientAgreements(clientId: string) {
  return useQuery({
    queryKey: ['agreements', '', clientId, false],
    queryFn: () =>
      api.get<{ agreements: AgreementRow[] }>('/agreements', { params: { clientId, active: 'true' } }),
    enabled: !!clientId,
  });
}

/**
 * Client → agreement, each with its own "New" beside it — the first two
 * questions of every invoice that sells something up front (Jesse,
 * 2026-09-30: *"select a client (or new client), select an existing agreement
 * or a new agreement"*). The new-client and new-agreement dialogs are the same
 * ones /clients and /agreements use, opened already on what this screen knows.
 */
export function ClientAgreementFields({
  preferTypes,
  newAgreementDefaults,
  errors,
  onCleared,
  agreementHint,
}: {
  /** Listed first, and auto-chosen when a client has exactly one. */
  preferTypes: AgreementType[];
  /** What "New agreement" opens holding, given the chosen client's name. */
  newAgreementDefaults: (clientName: string) => AgreementFormDefaults;
  errors?: { client?: string; agreement?: string };
  /** Called whenever the selection changes, so the form can drop its errors. */
  onCleared?: () => void;
  /** One line under the agreement — what the tab wants the reader to know about it. */
  agreementHint?: ReactNode;
}) {
  const clients = useInvoiceClients();
  const { clientId, agreementId, setTarget } = useInvoiceTarget();
  const agreements = useClientAgreements(clientId);
  const [clientOpen, setClientOpen] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);

  const clientName = clients.data?.find((c) => c.id === clientId)?.name ?? '';
  const rows = useMemo(() => {
    const list = agreements.data?.agreements ?? [];
    const rank = (t: AgreementType) => (preferTypes.includes(t) ? 0 : 1);
    return [...list].sort(
      (a, b) => rank(a.type) - rank(b.type) || a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  }, [agreements.data, preferTypes]);

  // Exactly one agreement of the kind this invoice sells → choose it. Never
  // guess between two, and only once per client so clearing it sticks.
  const autoPicked = useRef('');
  useEffect(() => {
    if (!clientId || agreementId || autoPicked.current === clientId || !agreements.data) return;
    autoPicked.current = clientId;
    const preferred = rows.filter((r) => preferTypes.includes(r.type));
    if (preferred.length === 1) setTarget({ clientId, agreementId: preferred[0].id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, agreementId, agreements.data]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="flex items-end gap-2 self-start" data-draft-field="client">
        <Select
          label="Client"
          className="min-w-0 flex-1"
          value={clientId}
          error={errors?.client}
          placeholder={clients.isLoading ? 'Loading clients…' : 'Choose a client…'}
          options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          onChange={(e) => {
            setTarget({ clientId: e.target.value, agreementId: '' });
            onCleared?.();
          }}
        />
        <Button
          type="button"
          variant="outline"
          className={errors?.client ? 'mb-6' : undefined}
          onClick={() => setClientOpen(true)}
          title="Create a client — from an Accounts organization or by hand"
        >
          <Plus className="h-4 w-4" aria-hidden />
          <span className="sr-only sm:not-sr-only">New client</span>
          <span className="sm:hidden" aria-hidden>New</span>
        </Button>
      </div>

      <div data-draft-field="agreement">
        <div className="flex items-end gap-2">
          <Select
            label="Agreement"
            className="min-w-0 flex-1"
            value={agreementId}
            error={errors?.agreement}
            disabled={!clientId}
            placeholder={
              !clientId
                ? 'Choose a client first'
                : agreements.isLoading
                  ? 'Loading agreements…'
                  : rows.length === 0
                    ? 'No active agreements — create one'
                    : 'Choose an agreement…'
            }
            options={rows.map((a) => ({
              value: a.id,
              label: a.name.toLowerCase().includes(TYPE_LABEL[a.type].toLowerCase())
                ? a.name
                : `${a.name} · ${TYPE_LABEL[a.type]}`,
            }))}
            onChange={(e) => {
              setTarget({ clientId, agreementId: e.target.value });
              onCleared?.();
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!clientId}
            className={errors?.agreement ? 'mb-6' : undefined}
            onClick={() => setAgreementOpen(true)}
            title={clientId ? `Create an agreement for ${clientName}` : 'Choose a client first'}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span className="sr-only sm:not-sr-only">New agreement</span>
            <span className="sm:hidden" aria-hidden>New</span>
          </Button>
        </div>
        {agreementHint && <div className="mt-1.5 text-caption text-grey-500">{agreementHint}</div>}
      </div>

      <NewClientModal
        open={clientOpen}
        onOpenChange={setClientOpen}
        onCreated={(id) => {
          // A brand-new client has no agreements, so the next question is
          // "which agreement?" — answered by making one.
          autoPicked.current = id;
          setTarget({ clientId: id, agreementId: '' });
          onCleared?.();
          setAgreementOpen(true);
        }}
      />
      <AgreementModal
        open={agreementOpen}
        onOpenChange={setAgreementOpen}
        defaults={{ clientId, ...newAgreementDefaults(clientName) }}
        onSaved={(id) => {
          setTarget({ clientId, agreementId: id });
          onCleared?.();
        }}
      />
    </div>
  );
}
